import { create } from 'zustand';
import type {
  BuildingId,
  CameraState,
  GameSnapshot,
  GameState,
  GridCoord,
  HoverState,
  ToolMode,
  Vec2,
  ZoneType
} from '../types/core';
import { hashString, mulberry32 } from '../utils/random';
import { createInitialGameState } from '../world/createInitialState';
import { updateConstruction } from './systems/buildings';
import { updateCitizens } from './systems/citizens';
import { updateEconomy } from './systems/economy';
import { updateProgression } from './systems/progression';
import { advanceTime } from './systems/time';
import { updateVehicles } from './systems/transport';
import { placeZoneBlock } from './systems/zoning';
import { updateWeather } from './systems/weather';

interface GameActions {
  readonly hydrate: (state?: GameState) => void;
  readonly markWelcomeSeen: () => void;
  readonly setTool: (tool: ToolMode) => void;
  readonly setHover: (hover: HoverState) => void;
  readonly selectBuilding: (buildingId?: BuildingId) => void;
  readonly beginDrag: (zone: ZoneType, coord: GridCoord) => void;
  readonly updateDrag: (coord: GridCoord) => void;
  readonly completeDrag: () => void;
  readonly cancelDrag: () => void;
  readonly tick: (deltaMinutes: number) => void;
  readonly setCamera: (camera: Partial<CameraState>) => void;
  readonly nudgeCamera: (dx: number, dz: number) => void;
  readonly rotateCamera: (delta: number) => void;
  readonly zoomCamera: (delta: number) => void;
  readonly setSpeed: (speed: GameState['time']['speed']) => void;
  readonly togglePaused: () => void;
  readonly setWalkActive: (active: boolean) => void;
  readonly moveWalk: (movement: Vec2, heading?: number) => void;
  readonly replaceFromSnapshot: (snapshot: GameSnapshot) => void;
  readonly snapshot: () => GameSnapshot;
}

export type GameStore = GameState & GameActions;

function serializableState(state: GameStore): GameState {
  return {
    hydrated: state.hydrated,
    hasSeenWelcome: state.hasSeenWelcome,
    selectedTool: state.selectedTool,
    hover: state.hover,
    selectedBuildingId: state.selectedBuildingId,
    drag: state.drag,
    world: state.world,
    time: state.time,
    economy: state.economy,
    progression: state.progression,
    weather: state.weather,
    camera: state.camera,
    walk: state.walk
  };
}

export const useGameStore = create<GameStore>()((set, get) => ({
  ...createInitialGameState(),

  hydrate: (state) =>
    set({
      ...(state ?? createInitialGameState()),
      hydrated: true
    }),

  markWelcomeSeen: () => set({ hasSeenWelcome: true }),

  setTool: (tool) =>
    set((state) => ({
      selectedTool: tool,
      drag: undefined,
      walk: {
        ...state.walk,
        active: tool === 'walk'
      }
    })),

  setHover: (hover) => set({ hover }),

  selectBuilding: (buildingId) => set({ selectedBuildingId: buildingId }),

  beginDrag: (zone, coord) =>
    set({
      drag: {
        zone,
        start: coord,
        current: coord
      }
    }),

  updateDrag: (coord) =>
    set((state) => ({
      drag: state.drag
        ? {
            ...state.drag,
            current: coord
          }
        : undefined
    })),

  completeDrag: () =>
    set((state) => {
      if (!state.drag) {
        return { drag: undefined };
      }

      const rng = mulberry32(hashString(`${state.time.day}-${state.time.minuteOfDay}-${state.world.buildings.length}`));
      const placement = placeZoneBlock(
        state.world.cells,
        state.world.buildings,
        state.economy,
        state.drag.zone,
        state.drag.start,
        state.drag.current,
        state.world.size,
        state.time.day,
        rng
      );

      return {
        drag: undefined,
        world: {
          ...state.world,
          cells: placement.cells,
          buildings: placement.buildings
        },
        economy: placement.economy
      };
    }),

  cancelDrag: () => set({ drag: undefined }),

  tick: (deltaMinutes) =>
    set((state) => {
      if (!state.hydrated || state.time.paused) {
        return state;
      }

      const rng = mulberry32(
        hashString(`${state.time.day}:${state.time.minuteOfDay}:${state.world.citizens.length}:${state.world.buildings.length}`)
      );
      const time = advanceTime(state.time, deltaMinutes);
      const constructedBuildings = updateConstruction(state.world.buildings, deltaMinutes * state.time.speed);
      const citizenResult = updateCitizens(
        state.world.citizens,
        constructedBuildings,
        state.world.cells,
        state.world.size,
        time.minuteOfDay,
        deltaMinutes * state.time.speed,
        rng
      );
      const progression = updateProgression(citizenResult.citizens.length);
      const vehicles = updateVehicles(
        state.world.vehicles,
        state.world.cells,
        state.world.size,
        citizenResult.citizens.length,
        progression,
        deltaMinutes * state.time.speed,
        rng
      );
      const economy = updateEconomy(
        state.economy,
        citizenResult.buildings,
        citizenResult.citizens,
        vehicles,
        deltaMinutes * state.time.speed
      );
      const weather = updateWeather(state.weather, time, rng);

      return {
        time,
        economy,
        progression,
        weather,
        world: {
          ...state.world,
          buildings: citizenResult.buildings,
          citizens: citizenResult.citizens,
          vehicles
        }
      };
    }),

  setCamera: (camera) =>
    set((state) => ({
      camera: {
        ...state.camera,
        ...camera
      }
    })),

  nudgeCamera: (dx, dz) =>
    set((state) => ({
      camera: {
        ...state.camera,
        target: {
          ...state.camera.target,
          x: state.camera.target.x + dx,
          z: state.camera.target.z + dz
        }
      }
    })),

  rotateCamera: (delta) =>
    set((state) => ({
      camera: {
        ...state.camera,
        rotation: state.camera.rotation + delta
      }
    })),

  zoomCamera: (delta) =>
    set((state) => ({
      camera: {
        ...state.camera,
        zoom: Math.max(10, Math.min(46, state.camera.zoom + delta))
      }
    })),

  setSpeed: (speed) =>
    set((state) => ({
      time: {
        ...state.time,
        speed
      }
    })),

  togglePaused: () =>
    set((state) => ({
      time: {
        ...state.time,
        paused: !state.time.paused
      }
    })),

  setWalkActive: (active) =>
    set((state) => ({
      selectedTool: active ? 'walk' : 'explore',
      walk: {
        ...state.walk,
        active
      }
    })),

  moveWalk: (movement, heading) =>
    set((state) => ({
      walk: {
        ...state.walk,
        position: {
          x: state.walk.position.x + movement.x,
          z: state.walk.position.z + movement.z
        },
        heading: heading ?? state.walk.heading
      }
    })),

  replaceFromSnapshot: (snapshot) =>
    set({
      ...snapshot.state,
      hydrated: true
    }),

  snapshot: () => ({
    version: 1,
    savedAt: new Date().toISOString(),
    state: serializableState(get())
  })
}));
